/* =========================================================================
   Pellington - PDP VIDEO: start on swipe-in, corner play/pause.
   19 September 2026. Fourth cut.

   Replaces the theme's own product-video autoplay (initVideoAutoplayTrigger +
   DeferredMedia.loadContent in assets/theme.js), which works in every browser
   a harness can reach and failed on the owner's iPhone. Three fragilities:

     1. An IntersectionObserver at rootMargin 0 watching an element inside a
        clipped, scroll-snapped track. Both sibling files already refuse to
        trust the engine on this same track - see the "deliberately NOT
        scrollLeft" notes in pell-card-swipe.js and pell-pdp-gallery.js. This
        file measures overlap with getBoundingClientRect instead.
     2. loadContent() clones the <video> into a DETACHED div, calls load()
        there, appends only on 'canplay'. WebKit need not fetch media for an
        element outside the document, and data-loaded is already true by then,
        so the poster tap is dead too.
     3. The muted ATTRIBUTE alone does not satisfy Safari's autoplay policy on
        a script-built element; the muted PROPERTY does.

   RULES THAT COST REAL DEBUGGING - DO NOT UNDO ANY OF THESE:

   ⚑ NO EXPLICIT load(). Appending a <video> that carries its <source>
     children runs resource selection on its own. load() then play() in one
     tick aborts the play promise - AbortError, measured, every time.

   ⚑ THE VIDEO'S VISIBILITY IS AN INLINE opacity SET HERE AND REMOVED ON
     'loadeddata'. It must not move to the stylesheet and must not get a
     transition: the CSS version left the transition permanently in the
     running state, so the video sat at opacity 0 while playing under the
     poster. `.deferred-media video` has a solid background, so an undecoded
     video paints cream over the photograph - that was the blank panel.

   ⚑ prefers-reduced-motion IS NOT HONOURED. Owner's decision, 19 Sep, after
     Reduce Motion turned out to be why the video would not start on their
     phone. This is a silent six-second product loop, not parallax. The
     obligation that bites is WCAG 2.2.2 Pause, Stop, Hide, and THE CORNER
     BUTTON IS THAT CONTROL - if the button ever goes, this gate comes back.

   ⚑ THE BUTTON TOGGLES ON ITS OWN data-playing, NEVER ON video.paused.
     play() sets paused=false SYNCHRONOUSLY, before the promise settles. Under
     Low Power Mode the touchend attempt therefore left paused=false for the
     few milliseconds until the rejection landed - and the click event fires
     inside that window, so the button read "playing" and paused it. That is
     the double-tap the owner reported on 19 Sep. data-playing is set from the
     'playing' event, which only fires on real playback.

   ⚑ THE FIRST START IS DECLARATIVE, NOT SCRIPTED. The element is inserted
     carrying autoplay/muted/playsinline/loop and the browser is left to start
     it; a scripted play() runs only as a fallback after DECLARATIVE_MS, on a
     button tap, or on a RETURN to the slide. Reason: analuisa.com autoplays
     under Low Power Mode with a plain <video src autoplay loop playsinline>
     whose muted is a JS property - in the initial DOM, never scripted. Their
     file even has an audio track, so "no audio track" is not the trick.
     WebKit judges scripted playback more strictly than declarative autoplay.
   ========================================================================= */
(function () {
  'use strict';

  window.__pellPdpVideo = true;
  var DBG = (window.__pellVidDbg = { slide: -1, active: -1, err: '', stripped: 0, tries: 0, ready: false, decl: false });

  var PLAY =
    '<svg class="pell-vid-btn__play" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
    '<path d="M4 2.2 13.2 8 4 13.8z"/></svg>';
  var PAUSE =
    '<svg class="pell-vid-btn__pause" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
    '<rect x="3.4" y="2.4" width="3.2" height="11.2" rx=".4"/>' +
    '<rect x="9.4" y="2.4" width="3.2" height="11.2" rx=".4"/></svg>';

  var MAX_TRIES = 4;
  var DECLARATIVE_MS = 900;

  function each(list, fn) { for (var i = 0; i < list.length; i++) fn(list[i], i); }
  function playing(st) { return st.btn.hasAttribute('data-playing'); }

  function galleries() {
    var list = document.querySelectorAll('.pdp--media-slideshow .slideshow');
    return list.length ? list : document.querySelectorAll('.media-gallery .slideshow');
  }

  function activeIndex(track) {
    var tr = track.getBoundingClientRect(), kids = track.children, best = 0, bo = -1;
    for (var i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      var o = Math.min(r.right, tr.right) - Math.max(r.left, tr.left);
      if (o > bo) { bo = o; best = i; }
    }
    return best;
  }

  /* Runs at script execution, not on DOMContentLoaded: deferred scripts run in
     document order before that event, so theme.js has REGISTERED
     initVideoAutoplayTrigger but not run it. Clearing the attribute now means
     its selector matches nothing and the two never fight over one <video>. */
  function disarm() {
    each(document.querySelectorAll('deferred-media[data-autoplay]'), function (el) {
      el.removeAttribute('data-autoplay');
      DBG.stripped++;
    });
  }
  disarm();

  function ensure(st) {
    if (st.v) return st.v;
    var tpl = st.dm.querySelector('template');
    var node = tpl && tpl.content.firstElementChild;
    if (!node) return null;
    var v = node.cloneNode(true);
    if (v.nodeName !== 'VIDEO') return null;

    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.loop = true;
    v.autoplay = true;
    v.preload = 'auto';
    v.setAttribute('autoplay', '');
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.removeAttribute('controls');
    v.style.opacity = '0';

    /* 'playing', not 'play' - see the data-playing rule in the header. */
    v.addEventListener('playing', function () {
      st.btn.setAttribute('data-playing', '');
      st.btn.setAttribute('aria-label', 'Pause video');
      DBG.err = '';
    });
    v.addEventListener('pause', function () {
      st.btn.removeAttribute('data-playing');
      st.btn.setAttribute('aria-label', 'Play video');
    });
    v.addEventListener('error', function () { DBG.err = 'media-error'; st.held = true; });

    v.addEventListener('loadeddata', function () {
      v.style.removeProperty('opacity');
      st.dm.setAttribute('data-ready', '');
      DBG.ready = true;
    });

    st.dm.appendChild(v);
    st.dm.setAttribute('data-loaded', 'true');
    st.v = v;

    /* Give the autoplay attribute its chance before asking. */
    clearTimeout(st.timer);
    st.timer = setTimeout(function () {
      DBG.decl = playing(st);
      if (st.want && !st.userPaused && !playing(st)) attempt(st, v);
    }, DECLARATIVE_MS);

    return v;
  }

  function attempt(st, v) {
    if (st.userPaused) return;
    if (st.tries >= MAX_TRIES) { st.held = true; return; }
    st.tries++;
    DBG.tries = st.tries;
    var p;
    try { p = v.play(); } catch (e) { note(st, e); return; }
    if (p && p['catch']) p['catch'](function (e) { note(st, e); });
  }

  function note(st, e) {
    DBG.err = (e && e.name) || 'reject';
    if (st.tries >= MAX_TRIES) st.held = true;
  }

  function sync(st) {
    var i = activeIndex(st.track);
    DBG.active = i;
    DBG.slide = st.i;
    var on = i === st.i;

    if (on) { st.btn.setAttribute('data-on', ''); } else { st.btn.removeAttribute('data-on'); }

    if (!on) {
      st.want = false;
      st.held = false;
      st.userPaused = false;
      st.tries = 0;
      clearTimeout(st.timer);
      if (st.v && !st.v.paused) st.v.pause();
      return;
    }
    if (st.held || st.userPaused) return;
    st.want = true;

    var first = !st.v;
    var v = ensure(st);
    if (!v) return;

    /* ⚑ A RETURN TO THE SLIDE MUST ASK DIRECTLY. ensure() arms the
       declarative fallback timer only on the call that CREATES the element,
       so without this a shopper who swiped away and came back would sit in
       front of a paused video for ever - found in testing, 19 Sep. By then
       the element has played once and is unlocked, so a scripted play is
       both allowed and instant; waiting out DECLARATIVE_MS again would only
       add lag. The FIRST start is still left to the attribute. */
    if (!first && !playing(st)) attempt(st, v);
  }

  function build(gal) {
    if (gal.__pellVid) return;
    var track = gal.querySelector('.slider--generic');
    var dm = gal.querySelector('deferred-media');
    if (!track || !dm) return;

    var idx = -1;
    for (var k = 0; k < track.children.length; k++) {
      if (track.children[k].contains(dm)) { idx = k; break; }
    }
    if (idx < 0) return;
    gal.__pellVid = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pell-vid-btn';
    btn.setAttribute('aria-label', 'Play video');
    btn.innerHTML = PLAY + PAUSE;

    var st = {
      track: track, dm: dm, btn: btn, i: idx,
      v: null, held: false, want: false, userPaused: false, tries: 0, timer: 0
    };
    gal.__pellVidState = st;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (playing(st)) {
        st.v.pause();
        st.userPaused = true;
        st.want = false;
        return;
      }
      st.userPaused = false;
      st.held = false;
      st.tries = 0;
      st.want = true;
      DBG.err = '';
      var v = ensure(st);
      if (v) attempt(st, v);
    });

    gal.appendChild(btn);
    sync(st);
  }

  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (!t || t.nodeType !== 1 || !t.classList) return;
    if (!t.classList.contains('slider--generic')) return;
    var gal = t.closest ? t.closest('.slideshow') : null;
    if (gal && gal.__pellVidState) sync(gal.__pellVidState);
  }, true);

  function gestureRetry() {
    each(galleries(), function (g) {
      var st = g.__pellVidState;
      if (!st || st.userPaused) return;
      st.held = false;
      st.tries = 0;
      sync(st);
    });
  }

  /* The first call is synchronous: iOS permits a play() started inside a
     user-gesture task where it refuses the same call from a timer, and the
     swipe that brings the video into view IS that gesture. The delayed pass is
     a second reading for a snap that has not settled by the time the finger
     lifts, not a replacement. */
  document.addEventListener('touchend', function () {
    gestureRetry();
    setTimeout(gestureRetry, 260);
  }, { passive: true, capture: true });

  window.addEventListener('resize', function () {
    each(galleries(), function (g) { if (g.__pellVidState) sync(g.__pellVidState); });
  }, { passive: true });

  document.addEventListener('visibilitychange', function () {
    each(galleries(), function (g) {
      var st = g.__pellVidState;
      if (!st) return;
      if (document.hidden) {
        if (st.v && !st.v.paused) st.v.pause();
      } else {
        st.held = false;
        st.tries = 0;
        sync(st);
      }
    });
  });

  document.addEventListener('variant:change', function () {
    setTimeout(function () { disarm(); each(galleries(), build); }, 0);
  });

  function init() { disarm(); each(galleries(), build); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
